import { useState, useEffect } from 'react'
import { useTuningStore, getAllPresets, exportPreset, importPreset } from '../store/tuningStore'
import type { Preset } from '../types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'

interface PresetManagerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const PresetManagerModal = ({ open, onOpenChange }: PresetManagerModalProps) => {
  const { loadPreset, savePreset, deletePreset, currentPreset } = useTuningStore()
  const [presets, setPresets] = useState<Preset[]>([])
  const [saveName, setSaveName] = useState('')
  const [showImport, setShowImport] = useState(false)
  const [importText, setImportText] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      loadPresets()
    }
  }, [open])

  useEffect(() => {
    if (currentPreset) {
      setSaveName(currentPreset)
    }
  }, [currentPreset])

  const loadPresets = () => {
    getAllPresets()
      .then(setPresets)
      .catch(() => setError('Erreur lors du chargement des gammes'))
  }

  const handleLoad = (preset: Preset) => {
    loadPreset(preset)
    setSaveName(preset.name)
    setError(null)
  }

  const handleSave = async () => {
    if (!saveName.trim()) {
      setError('Veuillez entrer un nom pour le preset')
      return
    }
    const trimmedName = saveName.trim()
    const existingPreset = presets.find((p) => p.name === trimmedName)
    if (existingPreset && existingPreset.name !== currentPreset && !confirm(`Un preset avec ce nom existe déjà. Le remplacer?`)) {
      return
    }
    try {
      await savePreset(trimmedName)
      if (trimmedName !== currentPreset) {
        setSaveName('')
      }
      loadPresets()
      setError(null)
    } catch (err) {
      setError('Erreur lors de la sauvegarde')
    }
  }

  const handleDelete = async (preset: Preset) => {
    if (preset.id === undefined) return
    if (confirm(`Supprimer le preset "${preset.name}"?`)) {
      try {
        await deletePreset(preset.id)
        loadPresets()
        setError(null)
      } catch (err) {
        setError('Erreur lors de la suppression')
      }
    }
  }

  const handleExport = (preset: Preset) => {
    const json = exportPreset(preset)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${preset.name.replace(/\s+/g, '_')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImport = () => {
    try {
      const preset = importPreset(importText)
      loadPreset(preset)
      setShowImport(false)
      setImportText('')
      setError(null)
    } catch (err) {
      setError('Format JSON invalide')
    }
  }

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const preset = importPreset(event.target?.result as string)
        loadPreset(preset)
        setError(null)
      } catch (err) {
        setError('Format JSON invalide')
      }
    }
    reader.readAsText(file)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gestion des Presets</DialogTitle>
          <DialogDescription>
            Sauvegardez, chargez et gérez vos presets de tuning
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {/* Save preset */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sauvegarder le preset actuel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="Nom du preset"
                  className="flex-1"
                />
                <Button onClick={handleSave}>
                  Sauvegarder
                </Button>
              </div>
              {currentPreset && (
                <Button
                  onClick={() => {
                    setSaveName(currentPreset)
                    handleSave()
                  }}
                  variant="secondary"
                  className="w-full"
                >
                  💾 Sauvegarder "{currentPreset}" (remplacer)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Presets list */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Presets disponibles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {presets.map((preset) => (
                  <div
                    key={preset.name}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      currentPreset === preset.name
                        ? 'bg-primary/10 border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{preset.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(preset.date).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleLoad(preset)}
                        variant="default"
                        size="sm"
                      >
                        Charger
                      </Button>
                      <Button
                        onClick={() => handleExport(preset)}
                        variant="outline"
                        size="sm"
                      >
                        Export
                      </Button>
                      {!preset.isDefault && (
                        <Button
                          onClick={() => handleDelete(preset)}
                          variant="destructive"
                          size="sm"
                        >
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Import */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Importer un preset</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                type="file"
                accept=".json"
                onChange={handleFileImport}
                className="cursor-pointer"
              />
              <Button
                onClick={() => setShowImport(!showImport)}
                variant="outline"
                className="w-full"
              >
                {showImport ? '▼ Masquer' : '▲ Coller JSON'}
              </Button>
              {showImport && (
                <div className="space-y-3">
                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder="Collez le JSON du preset ici..."
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring h-32 font-mono text-sm bg-background"
                  />
                  <Button
                    onClick={handleImport}
                    className="w-full"
                  >
                    ✓ Importer
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  )
}

